import React from 'react';
import classNames from 'classnames';
import { Spinner } from '@legion-ui/core';

type ButtonProps = {
  children: React.ReactNode;
  className?: string;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  type?: 'button' | 'submit' | 'reset';
  size?: 'sm' | 'md' | 'lg';
  variant?: 'solid' | 'outline' | 'soft' | 'transparent';
  color?: 'primary' | 'secondary' | 'error' | 'success' | 'warning';
  disabled?: boolean;
  loading?: boolean;
  onClick?: () => void;
};

const Button: React.FC<ButtonProps> = ({
  children,
  className,
  iconLeft,
  iconRight,
  type = 'button',
  size = 'md',
  variant = 'solid',
  color = 'primary',
  disabled = false,
  loading = false,
  onClick,
}) => {
  const baseClasses =
    'inline-flex items-center justify-center font-medium rounded focus:outline-none transition';

  const sizeClasses = {
    sm: 'px-2.5 py-1.5 text-xs',
    md: 'px-4 py-2 text-sm',
    lg: 'px-5 py-3 text-lg',
  };

  const variantClasses = {
    solid: 'shadow-sm',
    outline: 'border',
    soft: 'bg-opacity-20',
    transparent: 'bg-transparent',
  };

  const colorClasses = {
    primary:
      variant === 'solid'
        ? 'bg-[#0649CF] text-white hover:bg-[#0549CF]'
        : 'border-[#0649CF] text-[#0649CF] hover:bg-blue-50',
    secondary:
      variant === 'solid'
        ? 'bg-zinc-400 text-white hover:bg-zinc-500'
        : 'border-zinc-400 text-zinc-400 hover:bg-zinc-50',
    error:
      variant === 'solid'
        ? 'bg-red-600 text-white hover:bg-red-700'
        : 'border-red-600 text-red-600 hover:bg-red-50',
    success:
      variant === 'solid'
        ? 'bg-green-600 text-white hover:bg-green-700'
        : 'border-green-600 text-green-600 hover:bg-green-50',
    warning:
      variant === 'solid'
        ? 'bg-yellow-500 text-white hover:bg-yellow-600'
        : 'border-yellow-500 text-yellow-500 hover:bg-yellow-50',
  };

  const disabledClasses = 'opacity-50 cursor-not-allowed bg-zinc-400';
  const loadingClasses = 'cursor-wait';

  return (
    <button
      type={type}
      className={classNames(
        baseClasses,
        sizeClasses[size],
        variantClasses[variant],
        colorClasses[color],
        { [disabledClasses]: disabled, [loadingClasses]: loading },
        className,
      )}
      disabled={disabled || loading}
      onClick={onClick}
    >
      {loading && (
        <span className="mr-2">
          <Spinner size="sm" />
        </span>
      )}
      {iconLeft && !loading && <span className="mr-2">{iconLeft}</span>}
      {children}
      {iconRight && <span className="ml-2">{iconRight}</span>}
    </button>
  );
};

export default Button;
